// src/components/Utils/TTS.js
class TTSManager {
  constructor() {
    this.isSpeaking = false;
    this.currentIndex = 0;
    this.sentences = [];
  }

  // 언어 감지 함수
  detectLanguage(text) {
    // 각 언어의 문자 패턴
    const patterns = {
      'ko-KR': /[\uAC00-\uD7AF\u1100-\u11FF\u3130-\u318F]/,  // 한글
      'ja-JP': /[\u3040-\u309F\u30A0-\u30FF]/,              // 히라가나, 가타카나
      'zh-CN': /[\u4E00-\u9FFF]/,                           // 한자 (중국어)
      'ar-SA': /[\u0600-\u06FF]/,                           // 아랍어
      'ru-RU': /[\u0400-\u04FF]/,                           // 키릴 문자 (러시아어)
    };

    // 각 언어별 문자 개수 카운트
    const counts = {};
    for (const [lang, pattern] of Object.entries(patterns)) {
      const matches = text.match(new RegExp(pattern, 'g'));
      counts[lang] = matches ? matches.length : 0;
    }

    // 특수 문자 제외한 영문/라틴 문자 카운트
    const latinMatch = text.match(/[a-zA-ZÀ-ÿ]/g);
    const latinCount = latinMatch ? latinMatch.length : 0;

    // 가장 많이 등장하는 언어 찾기
    let maxLang = 'en-US'; // 기본값: 영어
    let maxCount = latinCount;

    for (const [lang, count] of Object.entries(counts)) {
      if (count > maxCount) {
        maxCount = count;
        maxLang = lang;
      }
    }

    // 라틴 문자 중 스페인어/프랑스어 구분
    if (maxLang === 'en-US' && latinCount > 0) {
      // 스페인어 특징: ñ, ¿, ¡
      if (/[ñÑ¿¡]/g.test(text)) {
        maxLang = 'es-ES';
      }
      // 프랑스어 특징: é, è, ê, à, ç, œ
      else if (/[éèêàùçœÉÈÊÀÙÇŒ]/g.test(text)) {
        maxLang = 'fr-FR';
      }
    }

    return maxLang;
  }

  // 문장 단위로 분리하는 함수
  splitIntoSentences(text) {
    // 문장 종결 부호로 분리 (., !, ?, 。, !, ?)
    return text
      .split(/([.!?。!?]+[\s\n]+)/)
      .reduce((acc, part, index, array) => {
        if (index % 2 === 0 && part.trim()) {
          const sentence = part + (array[index + 1] || '');
          acc.push(sentence.trim());
        }
        return acc;
      }, [])
      .filter(s => s.length > 0);
  }

  // HTML에서 텍스트만 추출하는 함수
  extractTextFromHTML(htmlString) {
    // 임시 DOM 요소 생성
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;
    
    // script, style 태그 제거
    const scripts = tempDiv.querySelectorAll('script, style');
    scripts.forEach(script => script.remove());
    
    // 텍스트만 추출
    return tempDiv.textContent || tempDiv.innerText || '';
  }

  // TTS 시작
  speak(text, onStart, onEnd, onError) {
    // 이미 읽고 있으면 중지
    if (this.isSpeaking) {
      this.stop();
      return false;
    }

    // HTML에서 텍스트만 추출
    const plainText = this.extractTextFromHTML(text).trim();

    if (!plainText) {
      alert('읽을 내용이 없습니다.');
      return false;
    }

    // 문장 단위로 분리
    this.sentences = this.splitIntoSentences(plainText);
    
    if (this.sentences.length === 0) {
      alert('읽을 내용이 없습니다.');
      return false;
    }

    this.isSpeaking = true;
    this.currentIndex = 0;
    if (onStart) onStart();

    const speakNextSentence = () => {
      if (this.currentIndex >= this.sentences.length) {
        this.isSpeaking = false;
        if (onEnd) onEnd();
        return;
      }

      const sentence = this.sentences[this.currentIndex];
      const detectedLang = this.detectLanguage(sentence);
      
      const utterance = new SpeechSynthesisUtterance(sentence);
      utterance.lang = detectedLang;
      utterance.rate = 1.0;
      utterance.pitch = 1.0;

      utterance.onend = () => {
        this.currentIndex++;
        speakNextSentence();
      };

      utterance.onerror = (event) => {
        console.error('TTS 오류:', event);
        if (onError) onError(event);
        this.currentIndex++;
        speakNextSentence();
      };

      window.speechSynthesis.speak(utterance);
    };

    speakNextSentence();
    return true;
  }

  // TTS 중지
  stop() {
    window.speechSynthesis.cancel();
    this.isSpeaking = false;
    this.currentIndex = 0;
    this.sentences = [];
  }

  // 현재 상태 확인
  getSpeakingState() {
    return this.isSpeaking;
  }
}

// 싱글톤 인스턴스 export
const ttsManager = new TTSManager();
export default ttsManager;